import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Users, Crown, Check } from 'lucide-react';
import { TransferRoute } from '@/hooks/useTransfers';
import { useVehicleImages } from '@/hooks/useVehicleImages';
import sedanImage from '@/assets/sedan-transfer.jpg';
import suvImage from '@/assets/suv-transfer.jpg';

interface TransferSearchResultsProps {
  selectedRoute: TransferRoute;
  passengers: number;
  tripType: 'oneway' | 'roundtrip';
  calculatePrice: (route: TransferRoute, passengers: number, serviceType: 'standard' | 'vip', tripType: 'oneway' | 'roundtrip') => number;
  fromName: string;
  toName: string;
}

export const TransferSearchResults = ({
  selectedRoute,
  passengers,
  tripType,
  calculatePrice,
  fromName,
  toName,
}: TransferSearchResultsProps) => {
  const navigate = useNavigate();
  const { getVehicleByPassengers } = useVehicleImages();

  const handleServiceSelect = (serviceType: 'standard' | 'vip') => {
    const price = calculatePrice(selectedRoute, passengers, serviceType, tripType);
    navigate('/booking', {
      state: {
        from: fromName,
        to: toName,
        passengers,
        tripType,
        serviceType,
        price,
        route: selectedRoute,
      },
    });
  };

  const standardVehicle = getVehicleByPassengers('standard', passengers);
  const vipVehicle = getVehicleByPassengers('vip', passengers);

  return (
    <div className="animate-fade-in">
      <h3 className="text-2xl font-bold mb-2">Available Services</h3>
      <p className="text-muted-foreground mb-6 text-sm">Choose the perfect service for your journey</p>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Standard Service Card */}
        <div className="bg-white rounded-2xl border overflow-hidden hover:shadow-lg transition-shadow duration-300">
          <div className="relative h-48 overflow-hidden">
            <img src={standardVehicle?.image_url || sedanImage} alt="Standard Transfer" className="w-full h-full object-cover" />
            <Badge className="absolute top-3 left-3 bg-primary text-primary-foreground text-xs">Standard</Badge>
            <div className="absolute bottom-3 right-3 flex items-center gap-1.5 bg-white/90 px-2.5 py-1 rounded-full text-xs font-medium">
              <Users className="h-3 w-3 text-primary" />
              {passengers} pax
            </div>
          </div>
          <div className="p-5">
            <h4 className="text-lg font-bold mb-2">Comfortable Ride</h4>
            <p className="text-muted-foreground text-sm mb-3">
              {standardVehicle?.description || "Comfortable sedan or similar"}
            </p>
            <ul className="space-y-1.5 mb-5">
              {(standardVehicle?.features || ['Professional driver', 'Air conditioning', 'Luggage included']).map((feature, index) => (
                <li key={index} className="flex items-center gap-2 text-muted-foreground text-sm">
                  <Check className="h-4 w-4 text-primary flex-shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
            <div className="mb-4">
              <p className="text-xs text-muted-foreground">From</p>
              <p className="text-3xl font-bold text-primary">
                ${calculatePrice(selectedRoute, passengers, 'standard', tripType).toFixed(2)}
              </p>
              <p className="text-xs text-muted-foreground">{tripType === 'roundtrip' ? 'Round trip' : 'One way'}</p>
            </div>
            <Button onClick={() => handleServiceSelect('standard')} className="w-full h-11 bg-primary hover:bg-primary/90 text-primary-foreground">
              Select Service
            </Button>
          </div>
        </div>

        {/* VIP Service Card */}
        {vipVehicle && (
          <div className="bg-white rounded-2xl border overflow-hidden hover:shadow-lg transition-shadow duration-300">
            <div className="relative h-48 overflow-hidden">
              <img src={vipVehicle.image_url || suvImage} alt="VIP Transfer" className="w-full h-full object-cover" />
              <Badge className="absolute top-3 left-3 bg-secondary text-secondary-foreground text-xs flex items-center gap-1">
                <Crown className="h-3 w-3" /> VIP
              </Badge>
              <div className="absolute bottom-3 right-3 flex items-center gap-1.5 bg-white/90 px-2.5 py-1 rounded-full text-xs font-medium">
                <Users className="h-3 w-3 text-secondary" />
                {passengers} pax
              </div>
            </div>
            <div className="p-5">
              <h4 className="text-lg font-bold mb-2">Luxury Experience</h4>
              <p className="text-muted-foreground text-sm mb-3">
                {vipVehicle.description || "Luxury SUV or similar"}
              </p>
              <ul className="space-y-1.5 mb-5">
                {vipVehicle.features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-2 text-muted-foreground text-sm">
                    <Check className="h-4 w-4 text-secondary flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <div className="mb-4">
                <p className="text-xs text-muted-foreground">From</p>
                <p className="text-3xl font-bold text-secondary">
                  ${calculatePrice(selectedRoute, passengers, 'vip', tripType).toFixed(2)}
                </p>
                <p className="text-xs text-muted-foreground">{tripType === 'roundtrip' ? 'Round trip' : 'One way'}</p>
              </div>
              <Button onClick={() => handleServiceSelect('vip')} className="w-full h-11 bg-secondary hover:bg-secondary/90 text-secondary-foreground">
                Select Service
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
